/**
 * Local-dev overrides for estate planning product-feature eligibility (development only).
 * Activate via hash query `?epEligibility=<mode>` on any route, infer from `?epMock=`, or set
 * `REACT_APP_ESTATE_PLANNING_DEV_MOCK_ELIGIBILITY` in config.js. Boot reconciliation in
 * `estatePlanningDevMock` applies the configured mode over a mode persisted by an earlier
 * session, so config.js stays authoritative once the URL names nothing.
 */
import {
  ESTATE_DEV_MOCK_ELIGIBILITY_URL_PARAM,
  ESTATE_DEV_MOCK_SCENARIO_URL_PARAM,
  getEstateDevMockHashParam,
  setEstateDevMockHashParam,
} from './estateDevMockHashQuery';
import {
  inferPostPurchaseMockEligibilityMode,
  isPostPurchaseMockScenarioId,
  resolvePostPurchaseMockScenarioId,
} from './postPurchaseMockScenarios';

export const ESTATE_PLANNING_ELIGIBILITY_MOCK_STORAGE_KEY = 'dsp.estatePlanning.eligibilityMock';

export type EstatePlanningEligibilityMockMode =
  | 'pre-purchase'
  | 'post-purchase-will'
  | 'post-purchase-trust'
  | 'hidden';

export const ESTATE_PLANNING_ELIGIBILITY_MOCK_MODES: EstatePlanningEligibilityMockMode[] = [
  'pre-purchase',
  'post-purchase-will',
  'post-purchase-trust',
  'hidden',
];

const ELIGIBILITY_MODE_LABELS: Record<EstatePlanningEligibilityMockMode, string> = {
  'pre-purchase': 'Pre-purchase upsell (UPGRADE, no AVAILABLE)',
  'post-purchase-will': 'Post-purchase will (estateWill AVAILABLE)',
  'post-purchase-trust': 'Post-purchase trust (estateTrust AVAILABLE)',
  hidden: 'Hidden (no estate planning access)',
};

export function isEstatePlanningEligibilityMockMode(
  value: string | null | undefined
): value is EstatePlanningEligibilityMockMode {
  return Boolean(value && ESTATE_PLANNING_ELIGIBILITY_MOCK_MODES.includes(value as EstatePlanningEligibilityMockMode));
}

/** Reads `epEligibility` from the hash query, localStorage, or an active `epMock` scenario. */
export function resolveEstatePlanningEligibilityMockMode(): EstatePlanningEligibilityMockMode | null {
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const urlEligibilityMode = getEstateDevMockHashParam(ESTATE_DEV_MOCK_ELIGIBILITY_URL_PARAM);
  if (isEstatePlanningEligibilityMockMode(urlEligibilityMode)) {
    return urlEligibilityMode;
  }

  const urlScenarioId = getEstateDevMockHashParam(ESTATE_DEV_MOCK_SCENARIO_URL_PARAM);
  if (isPostPurchaseMockScenarioId(urlScenarioId)) {
    return inferPostPurchaseMockEligibilityMode(urlScenarioId);
  }

  const storedEligibilityMode = window.localStorage.getItem(ESTATE_PLANNING_ELIGIBILITY_MOCK_STORAGE_KEY);
  if (isEstatePlanningEligibilityMockMode(storedEligibilityMode)) {
    return storedEligibilityMode;
  }

  const storedScenarioId = resolvePostPurchaseMockScenarioId();
  if (storedScenarioId) {
    return inferPostPurchaseMockEligibilityMode(storedScenarioId);
  }

  return null;
}

export function isEstatePlanningEligibilityMockEnabled(): boolean {
  return resolveEstatePlanningEligibilityMockMode() !== null;
}

function buildMockEstateWillFeature(
  featureStatus: 'UPGRADE' | 'AVAILABLE' | 'NOT_AVAILABLE',
  fulfillmentStatus: 'ENROLLED' | 'NOT_AVAILABLE' | 'PENDING' = 'NOT_AVAILABLE'
) {
  return {featureStatus, fulfillmentStatus};
}

/** Product-feature slice applied when a dev eligibility mock is active. */
export function getEstatePlanningEligibilityMockProductFeatures(): Pick<
  ProductFeatures,
  'estateWill' | 'estateTrust'
> | null {
  const mode = resolveEstatePlanningEligibilityMockMode();
  if (!mode) {
    return null;
  }

  switch (mode) {
    case 'pre-purchase':
      return {
        estateWill: buildMockEstateWillFeature('UPGRADE'),
        estateTrust: buildMockEstateWillFeature('UPGRADE'),
      };
    case 'post-purchase-will':
      return {
        estateWill: buildMockEstateWillFeature('AVAILABLE', 'ENROLLED'),
        estateTrust: buildMockEstateWillFeature('NOT_AVAILABLE'),
      };
    case 'post-purchase-trust':
      return {
        estateWill: buildMockEstateWillFeature('NOT_AVAILABLE'),
        estateTrust: buildMockEstateWillFeature('AVAILABLE', 'ENROLLED'),
      };
    case 'hidden':
      return {
        estateWill: buildMockEstateWillFeature('NOT_AVAILABLE'),
        estateTrust: buildMockEstateWillFeature('NOT_AVAILABLE'),
      };
    default:
      return null;
  }
}

/** Merges dev eligibility overrides into the member's `productFeatures` object. */
export function withEstatePlanningEligibilityMock(productFeatures: ProductFeatures | {}): ProductFeatures | {} {
  const mockFeatures = getEstatePlanningEligibilityMockProductFeatures();
  if (!mockFeatures) {
    return productFeatures;
  }

  return {
    ...productFeatures,
    estateWill: mockFeatures.estateWill,
    estateTrust: mockFeatures.estateTrust,
  };
}

export function applyEstatePlanningEligibilityMockMode(mode: EstatePlanningEligibilityMockMode): void {
  window.localStorage.setItem(ESTATE_PLANNING_ELIGIBILITY_MOCK_STORAGE_KEY, mode);
}

export function clearEstatePlanningEligibilityMockMode(): void {
  window.localStorage.removeItem(ESTATE_PLANNING_ELIGIBILITY_MOCK_STORAGE_KEY);
}

export function getEstatePlanningEligibilityMockModeLabel(mode: EstatePlanningEligibilityMockMode): string {
  return ELIGIBILITY_MODE_LABELS[mode];
}

/** Extends `window.__dspEstatePlanningMock` with eligibility controls (call after post-purchase register). */
export function registerEstatePlanningEligibilityDevConsole(): void {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }

  const existingMock = window.__dspEstatePlanningMock ?? {
    scenarios: [],
    current: null,
    apply: () => undefined,
    clear: () => undefined,
  };

  window.__dspEstatePlanningMock = {
    ...existingMock,
    eligibilityModes: ESTATE_PLANNING_ELIGIBILITY_MOCK_MODES.map((id) => ({
      id,
      label: ELIGIBILITY_MODE_LABELS[id],
    })),
    eligibility: resolveEstatePlanningEligibilityMockMode(),
    applyEligibility(mode: string) {
      if (!isEstatePlanningEligibilityMockMode(mode)) {
        // eslint-disable-next-line no-console
        console.error(
          `[estate-planning mock] Unknown eligibility "${mode}". ` +
            `Use one of: ${ESTATE_PLANNING_ELIGIBILITY_MOCK_MODES.join(', ')}`
        );
        return;
      }

      applyEstatePlanningEligibilityMockMode(mode);
      setEstateDevMockHashParam(ESTATE_DEV_MOCK_ELIGIBILITY_URL_PARAM, mode);
      window.location.reload();
    },
    clearEligibility() {
      clearEstatePlanningEligibilityMockMode();
      setEstateDevMockHashParam(ESTATE_DEV_MOCK_ELIGIBILITY_URL_PARAM, null);
      window.location.reload();
    },
  };
}
